import React, { useCallback, useRef, useState } from "react";
import { AlertTriangle, Bold, Download, Eye, FileText, Film, Heading, Italic, Link2, List, Lock, Paperclip, PenLine, Plus, Trash2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import useAdminLoad from "../../hooks/useAdminLoad";
import ReadingMarkup from "../../lib/readingMarkup";
import { videoEmbedUrl } from "../../lib/videoUrls";
import {
  CHOICE_MAX,
  CHOICES_MAX,
  CHOICES_MIN,
  DURATION_MAX,
  LOCATION_NOTE_MAX,
  PRACTICE_INSTRUCTIONS_MAX,
  QUESTION_MAX,
  QUESTIONS_MAX,
  READING_BODY_MAX,
  RESOURCES_MAX,
  STEP_KINDS,
  STEP_TITLE_MAX,
  courseDurationMinutes,
  importedQuestions,
  isIdentityLocked,
  newQuestion,
  validateStep,
} from "../../pathways/builderModel";
import { listBookingServices, listCourseQuizzes, listCourses } from "../../pathways/adminApi";
import { resolveApiUrl } from "../../pathways/learnerApi";
import MediaPickerModal from "../MediaPickerModal";
import ModalShell from "./ModalShell";
import { browserTimeZone, fmtFileSize, isoToLocalInput, localInputToIso } from "./format";
import { kindIcon, kindLabel } from "./labels";
import { applyReadingTool, READING_TOOLS } from "./readingToolbar";

const TOOL_ICONS = Object.freeze({ bold: Bold, italic: Italic, heading: Heading, bullets: List, link: Link2 });

/** A library asset (MediaPickerModal's `asset`) as the draft's MediaRef. */
const assetToRef = (asset) => ({
  media_id: asset.id,
  filename: asset.original_name || asset.filename || "",
  size: Number(asset.size) || 0,
  content_type: asset.content_type || "",
  url: asset.url || "",
});


/**
 * The step editor (spec screen 3, contract §2 `Step`), a dialog over the builder.
 *
 *   Title                                   Duration (min)
 *   [ Video | Reading | Practice/Lab | Quiz | Live session | Course ]   (contract §10.0.1)
 *   Video     upload (media library, chunked above 5 MB) or a YouTube / Vimeo link;
 *             "Auto-complete at 90% watched"
 *   Reading   a formatting toolbar over the safe reading format (Write / Preview through
 *             the learner's own renderer), or an attached PDF; "Learner taps Mark as done"
 *   Practice  instruction text + "Require submission (text/photo)"
 *   Quiz      questions, 2-6 choices each with one correct radio, "+ Add question", the
 *             pass threshold %, and "Import from a course quiz" (a copy of its MCQs)
 *   Live      cohort: a date and time in the browser's zone, stored as UTC;
 *             self-paced: "Session type ▾" (the practice's active booking services),
 *             "booked separately"; both: a location note
 *   Course    one of the practice's PUBLISHED courses; a paid one is flagged, because
 *             joining the pathway grants the course (contract §10.0.5)
 *   Downloadable resources (optional) · Attach files (up to 10)          [Save step]
 *
 * It edits a COPY of the step: nothing reaches the draft until "Save step", which runs the
 * shape rules first (builderModel.validateStep). Switching the type of a NEW step keeps
 * what the other types held (the draft carries all six), and only the chosen type is
 * sent. A SAVED step's type is not offered for change at all, and a saved course step's
 * course is fixed: the server refuses either change (STEP_IDENTITY_CHANGED), so the
 * editor says so instead of letting the save fail.
 *
 * Choosing a session type or a course PREFILLS the duration (the service's
 * `duration_min`, the sum of the course's lesson durations, contract §10.1); the author
 * may still change it. The server never recomputes it.
 *
 * @param {object}  props
 * @param {object|null} props.editing   `{ levelKey, step, isNew }`, or null when closed
 * @param {(levelKey: string, step: object) => void} props.onSave
 * @param {() => void} props.onClose
 * @param {() => string} props.nextKey   client-only keys for new questions
 * @param {"cohort"|"self_paced"} props.schedule  the pathway's schedule: which live panel shows
 * @param {boolean} props.disabled
 */
export default function StepEditorModal({ editing, onSave, onClose, nextKey, schedule, disabled }) {
  if (!editing) return null;
  return (
    <Editor
      // A new step, or another step, starts from its own copy.
      key={editing.step.key}
      levelKey={editing.levelKey}
      initial={editing.step}
      isNew={editing.isNew}
      onSave={onSave}
      onClose={onClose}
      nextKey={nextKey}
      schedule={schedule}
      disabled={disabled}
    />
  );
}

function Editor({ levelKey, initial, isNew, onSave, onClose, nextKey, schedule, disabled }) {
  const t = useT("admin");
  const [step, setStep] = useState(initial);
  const [errors, setErrors] = useState({});
  const [picker, setPicker] = useState(null); // "video" | "pdf" | "resource" | null
  const identityLocked = isIdentityLocked(initial);

  const patch = (fields, clear = []) => {
    setStep((s) => ({ ...s, ...fields }));
    if (clear.length) setErrors((e) => { const n = { ...e }; clear.forEach((k) => delete n[k]); return n; });
  };
  const err = (key) => (errors[key] ? t(errors[key]) : "");

  const save = () => {
    const problems = validateStep(step);
    setErrors(problems);
    if (Object.keys(problems).length > 0) return;
    onSave(levelKey, step);
  };

  // Escape and the backdrop close the editor, but never from under the media picker, and
  // never silently over edits that "Save step" has not kept.
  const requestClose = () => {
    if (picker !== null) return;
    if (step !== initial && !window.confirm(t("path.editor.confirmDiscard"))) return;
    onClose();
  };

  const onPicked = (_url, asset) => {
    const ref = assetToRef(asset);
    if (picker === "video") patch({ video: { source: "upload", media: ref, url: "" } }, ["video"]);
    else if (picker === "pdf") patch({ reading: { ...step.reading, pdf: ref } });
    else if (picker === "resource" && !step.resources.some((r) => r.media_id === ref.media_id)) {
      patch({ resources: [...step.resources, ref] }, ["resources"]);
    }
  };

  return (
    <ModalShell open onClose={requestClose} labelledBy="pathway-step-editor-heading" testId={IDS.stepEditor} panelClassName="max-w-3xl">
      {/* Sticky: a long reading preview or a many-question quiz scrolls the dialog, and the
          way out (and the heading that says what this is) must stay on screen. */}
      <div className="sticky top-0 z-10 flex items-start justify-between gap-3 rounded-t-3xl bg-white px-5 pt-5 pb-3 sm:px-6 sm:pt-6">
        <h2 id="pathway-step-editor-heading" className="font-display text-2xl text-eh-ink">
          {isNew ? t("path.editor.newTitle") : t("path.editor.editTitle")}
        </h2>
        <button type="button" onClick={requestClose} aria-label={t("path.editor.close")} data-testid={IDS.stepClose} className="p-1.5 rounded-full text-eh-muted hover:bg-eh-cream hover:text-eh-ink">
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </div>
      <div className="px-5 pb-5 pt-2 sm:px-6 sm:pb-6 space-y-5">

        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]">
          <div>
            <label htmlFor="pathway-step-title" className="block text-sm text-eh-muted mb-1.5">{t("path.editor.title")}</label>
            <Input
              id="pathway-step-title"
              value={step.title}
              onChange={(e) => patch({ title: e.target.value }, ["title"])}
              maxLength={STEP_TITLE_MAX}
              disabled={disabled}
              autoFocus
              aria-invalid={Boolean(errors.title)}
              data-testid={IDS.stepTitle}
              className={errors.title ? invalidInputClass : ""}
            />
            <FieldError message={err("title")} testId={IDS.stepError("title")} />
          </div>
          <div>
            <label htmlFor="pathway-step-duration" className="block text-sm text-eh-muted mb-1.5">{t("path.editor.duration")}</label>
            <Input
              id="pathway-step-duration"
              type="number"
              inputMode="numeric"
              min="0"
              max={DURATION_MAX}
              value={step.duration_minutes}
              onChange={(e) => patch({ duration_minutes: e.target.value }, ["duration"])}
              disabled={disabled}
              data-testid={IDS.stepDurationInput}
              className={errors.duration ? invalidInputClass : ""}
            />
            <FieldError message={err("duration")} testId={IDS.stepError("duration")} />
          </div>
        </div>

        {identityLocked ? (
          <KindLocked kind={step.kind} />
        ) : (
          <div role="radiogroup" aria-label={t("path.editor.kind")} className="flex flex-wrap rounded-3xl border border-eh-border-soft overflow-hidden w-fit max-w-full">
            {STEP_KINDS.map((kind) => {
              const Icon = kindIcon(kind);
              const on = step.kind === kind;
              return (
                <button
                  key={kind}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  disabled={disabled}
                  onClick={() => patch({ kind })}
                  data-testid={IDS.stepKind(kind)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm border-r last:border-r-0 border-eh-border-soft ${
                    on ? "bg-eh-ochre/15 text-eh-ink font-semibold" : "bg-white text-eh-muted hover:text-eh-ink"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {kindLabel(t, kind)}
                </button>
              );
            })}
          </div>
        )}

        {step.kind === "video" && (
          <VideoPanel step={step} patch={patch} disabled={disabled} error={err("video")} onPick={() => setPicker("video")} />
        )}
        {step.kind === "reading" && (
          <ReadingPanel step={step} patch={patch} disabled={disabled} error={err("body")} onPick={() => setPicker("pdf")} />
        )}
        {step.kind === "practice" && (
          <PracticePanel step={step} patch={patch} disabled={disabled} error={err("instructions")} />
        )}
        {step.kind === "quiz" && (
          <QuizPanel step={step} patch={patch} disabled={disabled} nextKey={nextKey} errors={{ questions: err("questions"), threshold: err("threshold") }} />
        )}
        {step.kind === "live" && (
          <LivePanel
            step={step}
            patch={patch}
            schedule={schedule}
            disabled={disabled}
            errors={{ location: err("location"), startsAt: err("startsAt") }}
          />
        )}
        {step.kind === "course" && (
          <CoursePanel step={step} patch={patch} locked={identityLocked} disabled={disabled} error={err("course")} />
        )}

        <div className="rounded-2xl bg-eh-cream/70 border border-eh-border-soft px-4 py-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-eh-ink">
              <span className="font-semibold">{t("path.editor.resources")}</span>{" "}
              <span className="text-eh-muted">{t("path.editor.resourcesOptional")}</span>
              {" · "}
              <button
                type="button"
                onClick={() => setPicker("resource")}
                disabled={disabled || step.resources.length >= RESOURCES_MAX}
                data-testid={IDS.resourcesAttach}
                className="inline-flex items-center gap-1 font-semibold text-eh-ochre-deep hover:text-eh-ink disabled:opacity-40"
              >
                <Paperclip className="w-3.5 h-3.5" aria-hidden="true" /> {t("path.editor.attachFiles")}
              </button>
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={requestClose} data-testid={IDS.stepCancel}>{t("path.editor.cancel")}</Button>
              <Button onClick={save} disabled={disabled} data-testid={IDS.stepSave} className="bg-eh-ink text-eh-cream hover:bg-eh-ink/90">
                {t("path.editor.save")}
              </Button>
            </div>
          </div>
          {step.resources.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {step.resources.map((r) => (
                <FileChip
                  key={r.media_id}
                  file={r}
                  testId={IDS.resource(r.media_id)}
                  removeTestId={IDS.resourceRemove(r.media_id)}
                  onRemove={() => patch({ resources: step.resources.filter((x) => x.media_id !== r.media_id) }, ["resources"])}
                  disabled={disabled}
                />
              ))}
            </ul>
          )}
          <FieldError message={err("resources")} />
        </div>
      </div>

      {/* Outside the spaced column: `space-y-5` would give this fixed overlay a top margin
          and start it 20px down the viewport. */}
      <MediaPickerModal
        isOpen={picker !== null}
        accept={picker || "image"}
        onClose={() => setPicker(null)}
        onSelect={onPicked}
      />
    </ModalShell>
  );
}

function FileChip({ file, testId, removeTestId, onRemove, disabled }) {
  const t = useT("admin");
  const size = fmtFileSize(file.size, t);
  return (
    <li data-testid={testId} className="inline-flex items-center gap-2 rounded-full bg-white border border-eh-border-soft pl-3 pr-1 py-1 text-xs text-eh-ink">
      <FileText className="w-3.5 h-3.5 text-eh-muted" aria-hidden="true" />
      <span className="max-w-[14rem] truncate">{file.filename || t("path.editor.unnamedFile")}</span>
      {size && <span className="text-eh-muted">{size}</span>}
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={t("path.editor.removeFile", { name: file.filename })}
        data-testid={removeTestId}
        className="p-1 rounded-full text-eh-muted hover:text-rose-600 hover:bg-rose-50"
      >
        <X className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
    </li>
  );
}

function VideoPanel({ step, patch, disabled, error, onPick }) {
  const t = useT("admin");
  const v = step.video;
  const embed = v.source === "link" ? videoEmbedUrl(v.url.trim()) : "";
  return (
    <section aria-labelledby="pathway-video-heading" className="rounded-2xl border border-eh-border-soft p-4 space-y-3">
      <h3 id="pathway-video-heading" className="text-sm font-semibold text-eh-ink">{t("path.kind.video")}</h3>
      <div className="rounded-2xl border-2 border-dashed border-eh-border p-4 space-y-3">
        <p className="text-sm text-center text-eh-ink">{t("path.editor.video.uploadOrLink")}</p>
        {v.source === "upload" && v.media ? (
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid={IDS.videoFile} className="inline-flex items-center gap-2 text-sm text-eh-ink">
              <Film className="w-4 h-4 text-eh-muted" aria-hidden="true" />
              {v.media.filename || t("path.editor.video.uploaded")}
            </span>
            <button
              type="button"
              onClick={() => patch({ video: { source: "upload", media: null, url: "" } })}
              disabled={disabled}
              data-testid={IDS.videoRemove}
              className="text-xs text-rose-700 underline"
            >
              {t("path.editor.remove")}
            </button>
          </div>
        ) : (
          <div className="flex justify-center">
            <Button variant="outline" onClick={onPick} disabled={disabled} data-testid={IDS.videoUpload}>
              <Upload className="w-4 h-4 mr-2" aria-hidden="true" /> {t("path.editor.video.upload")}
            </Button>
          </div>
        )}
        <div>
          <label htmlFor="pathway-video-link" className="block text-xs text-eh-muted mb-1">{t("path.editor.video.link")}</label>
          <Input
            id="pathway-video-link"
            type="url"
            value={v.source === "link" ? v.url : ""}
            onChange={(e) => {
              const url = e.target.value;
              patch({ video: url.trim() ? { source: "link", media: null, url } : { source: "upload", media: null, url: "" } }, ["video"]);
            }}
            placeholder="https://www.youtube.com/watch?v="
            disabled={disabled}
            aria-invalid={Boolean(error)}
            data-testid={IDS.videoLink}
            className={error ? invalidInputClass : ""}
          />
          <FieldError message={error} testId={IDS.stepError("video")} />
        </div>
      </div>
      {embed && (
        <div className="aspect-video w-full max-w-md overflow-hidden rounded-lg border border-eh-border-soft bg-eh-ink">
          <iframe
            src={embed}
            title={step.title.trim() || t("path.editor.video.preview")}
            data-testid={IDS.videoPreview}
            className="w-full h-full"
            allow="fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      )}
      {v.source === "upload" && v.media?.url && (
        <video src={resolveApiUrl(v.media.url)} controls preload="metadata" data-testid={IDS.videoPreview} className="w-full max-w-md rounded-lg border border-eh-border-soft" />
      )}
      <p className="text-sm text-eh-muted">{t("path.editor.video.autoComplete")}</p>
    </section>
  );
}

function ReadingPanel({ step, patch, disabled, error, onPick }) {
  const t = useT("admin");
  const [previewing, setPreviewing] = useState(false);
  const area = useRef(null);
  const r = step.reading;

  const setBody = (body) => patch({ reading: { ...r, body } }, ["body"]);
  const applyTool = (tool) => {
    const el = area.current;
    const start = el ? el.selectionStart : r.body.length;
    const end = el ? el.selectionEnd : r.body.length;
    const next = applyReadingTool(tool, r.body, start, end);
    setBody(next.value);
    // Restore the caret once React has written the new value.
    requestAnimationFrame(() => {
      if (!area.current) return;
      area.current.focus();
      area.current.setSelectionRange(next.start, next.end);
    });
  };

  return (
    <section aria-labelledby="pathway-reading-heading" className="rounded-2xl border border-eh-border-soft p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="pathway-reading-heading" className="text-sm font-semibold text-eh-ink">{t("path.kind.reading")}</h3>
        <div role="group" aria-label={t("path.editor.reading.mode")} className="inline-flex rounded-md border border-eh-border-soft overflow-hidden text-xs">
          <ModeButton active={!previewing} onClick={() => setPreviewing(false)} icon={PenLine} testId={IDS.readingWriteToggle}>{t("path.editor.reading.write")}</ModeButton>
          <ModeButton active={previewing} onClick={() => setPreviewing(true)} icon={Eye} testId={IDS.readingPreviewToggle}>{t("path.editor.reading.preview")}</ModeButton>
        </div>
      </div>

      {previewing ? (
        // Bounded, with its own scroller: an unbounded preview grew the dialog by the whole
        // reading and carried the Write / Preview switch off the top of the screen.
        <div className="rounded-md border border-eh-border-soft bg-white px-4 py-2 min-h-[8rem] max-h-[60vh] overflow-y-auto" data-testid={IDS.readingPreviewPane}>
          {r.body.trim()
            ? <ReadingMarkup body={r.body} testId={IDS.readingPreview} className="text-sm" />
            : <p className="py-3 text-sm text-eh-muted" data-testid={IDS.readingPreview}>{t("path.editor.reading.previewEmpty")}</p>}
        </div>
      ) : (
        <div className="rounded-md border border-input bg-white">
          <div role="toolbar" aria-label={t("path.editor.reading.toolbar")} className="flex items-center gap-0.5 border-b border-eh-border-soft px-1.5 py-1">
            {READING_TOOLS.map((tool) => {
              const Icon = TOOL_ICONS[tool];
              return (
                <button
                  key={tool}
                  type="button"
                  onClick={() => applyTool(tool)}
                  disabled={disabled}
                  aria-label={t(`path.editor.reading.tool.${tool}`)}
                  title={t(`path.editor.reading.tool.${tool}`)}
                  data-testid={IDS.readingTool(tool)}
                  className="p-1.5 rounded text-eh-muted hover:text-eh-ink hover:bg-eh-cream disabled:opacity-40"
                >
                  <Icon className="w-4 h-4" aria-hidden="true" />
                </button>
              );
            })}
          </div>
          <label htmlFor="pathway-reading-body" className="sr-only">{t("path.editor.reading.body")}</label>
          <textarea
            id="pathway-reading-body"
            ref={area}
            value={r.body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            maxLength={READING_BODY_MAX}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            data-testid={IDS.readingBody}
            className={`w-full rounded-b-md px-3 py-2 text-sm leading-relaxed resize-y outline-none ${error ? invalidInputClass : ""}`}
          />
        </div>
      )}
      <FieldError message={error} testId={IDS.stepError("body")} />

      <div className="flex flex-wrap items-center gap-2 text-sm text-eh-ink">
        <span className="text-eh-muted">{t("path.editor.reading.or")}</span>
        {r.pdf ? (
          <ul className="flex">
            <FileChip
              file={r.pdf}
              testId={IDS.readingPdf}
              removeTestId={IDS.readingPdfRemove}
              onRemove={() => patch({ reading: { ...r, pdf: null } })}
              disabled={disabled}
            />
          </ul>
        ) : (
          <button
            type="button"
            onClick={onPick}
            disabled={disabled}
            data-testid={IDS.readingPdfAttach}
            className="inline-flex items-center gap-1 font-semibold text-eh-ochre-deep hover:text-eh-ink disabled:opacity-40"
          >
            <Paperclip className="w-3.5 h-3.5" aria-hidden="true" /> {t("path.editor.reading.attachPdf")}
          </button>
        )}
      </div>
      <p className="text-sm text-eh-muted">{t("path.editor.reading.markDone")}</p>
    </section>
  );
}

function ModeButton({ active, onClick, icon: Icon, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2.5 py-1 ${active ? "bg-eh-ink text-eh-cream" : "bg-white text-eh-ink hover:bg-eh-cream"}`}
    >
      <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {children}
    </button>
  );
}

function QuizPanel({ step, patch, disabled, nextKey, errors }) {
  const t = useT("admin");
  const quiz = step.quiz;
  const setQuiz = (fields, clear) => patch({ quiz: { ...quiz, ...fields } }, clear);
  const setQuestions = (questions) => setQuiz({ questions }, ["questions"]);
  const updateQuestion = (i, fields) => setQuestions(quiz.questions.map((q, j) => (j === i ? { ...q, ...fields } : q)));

  const removeChoice = (i, c) => {
    const q = quiz.questions[i];
    if (q.choices.length <= CHOICES_MIN) return;
    const choices = q.choices.filter((_, j) => j !== c);
    // The correct answer follows its choice; removing it falls back to the first.
    const correct = q.correct_index === c ? 0 : q.correct_index > c ? q.correct_index - 1 : q.correct_index;
    updateQuestion(i, { choices, correct_index: correct });
  };

  return (
    <section aria-labelledby="pathway-quiz-heading" className="rounded-2xl border border-eh-border-soft p-4 space-y-4">
      <h3 id="pathway-quiz-heading" className="text-sm font-semibold text-eh-ink">{t("path.kind.quiz")}</h3>
      {quiz.questions.length === 0 && <p className="text-sm text-eh-muted">{t("path.editor.quiz.empty")}</p>}
      <ol className="space-y-4">
        {quiz.questions.map((q, i) => (
          <li key={q.key} className="space-y-2">
            <div className="flex items-center gap-2">
              <label htmlFor={`pathway-quiz-q-${q.key}`} className="text-sm font-semibold text-eh-ink whitespace-nowrap">
                {t("path.editor.quiz.questionN", { n: i + 1 })}
              </label>
              <Input
                id={`pathway-quiz-q-${q.key}`}
                value={q.question}
                onChange={(e) => updateQuestion(i, { question: e.target.value })}
                maxLength={QUESTION_MAX}
                disabled={disabled}
                placeholder={t("path.editor.quiz.questionPh")}
                data-testid={IDS.quizQuestion(i)}
              />
              <button
                type="button"
                onClick={() => setQuestions(quiz.questions.filter((_, j) => j !== i))}
                disabled={disabled}
                aria-label={t("path.editor.quiz.removeQuestion", { n: i + 1 })}
                title={t("path.editor.quiz.removeQuestion", { n: i + 1 })}
                data-testid={IDS.quizRemoveQuestion(i)}
                className="p-2 rounded text-rose-600 hover:bg-rose-50 disabled:opacity-30"
              >
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
            <fieldset className="ml-6 space-y-1.5">
              <legend className="sr-only">{t("path.editor.quiz.choicesOf", { n: i + 1 })}</legend>
              {q.choices.map((choice, c) => (
                // Position IS identity: choices have no id of their own.
                <div key={c} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`pathway-quiz-correct-${q.key}`}
                    checked={q.correct_index === c}
                    onChange={() => updateQuestion(i, { correct_index: c })}
                    disabled={disabled}
                    aria-label={t("path.editor.quiz.markCorrect", { n: c + 1 })}
                    data-testid={IDS.quizCorrect(i, c)}
                    className="accent-eh-sage-deep"
                  />
                  <Input
                    value={choice}
                    onChange={(e) => updateQuestion(i, { choices: q.choices.map((x, j) => (j === c ? e.target.value : x)) })}
                    maxLength={CHOICE_MAX}
                    disabled={disabled}
                    placeholder={t("path.editor.quiz.choicePh", { n: c + 1 })}
                    aria-label={t("path.editor.quiz.choiceN", { n: c + 1 })}
                    data-testid={IDS.quizChoice(i, c)}
                    className="h-9"
                  />
                  {q.correct_index === c && <span className="text-xs text-emerald-800 whitespace-nowrap">{t("path.editor.quiz.correct")}</span>}
                  <button
                    type="button"
                    onClick={() => removeChoice(i, c)}
                    disabled={disabled || q.choices.length <= CHOICES_MIN}
                    aria-label={t("path.editor.quiz.removeChoice", { n: c + 1 })}
                    data-testid={IDS.quizRemoveChoice(i, c)}
                    className="p-1.5 rounded text-eh-muted hover:text-rose-600 disabled:opacity-30"
                  >
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => updateQuestion(i, { choices: [...q.choices, ""] })}
                disabled={disabled || q.choices.length >= CHOICES_MAX}
                data-testid={IDS.quizAddChoice(i)}
                className="text-xs font-semibold text-eh-sage-deep hover:text-eh-ink disabled:opacity-40"
              >
                {t("path.editor.quiz.addChoice")}
              </button>
            </fieldset>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={() => setQuestions([...quiz.questions, newQuestion(nextKey())])}
        disabled={disabled || quiz.questions.length >= QUESTIONS_MAX}
        data-testid={IDS.quizAddQuestion}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-eh-ochre-deep hover:text-eh-ink disabled:opacity-40"
      >
        <Plus className="w-4 h-4" aria-hidden="true" /> {t("path.editor.quiz.addQuestion")}
      </button>
      <FieldError message={errors.questions} testId={IDS.stepError("questions")} />
      <QuizImport
        disabled={disabled}
        onImport={(courseQuiz) => setQuestions([...quiz.questions, ...importedQuestions(courseQuiz, nextKey)])}
      />
      <div className="flex items-center gap-3">
        <label htmlFor="pathway-quiz-threshold" className="text-sm text-eh-ink">{t("path.editor.quiz.threshold")}</label>
        <div className="inline-flex items-center gap-1">
          <Input
            id="pathway-quiz-threshold"
            type="number"
            inputMode="numeric"
            min="0"
            max="100"
            value={quiz.pass_threshold}
            onChange={(e) => setQuiz({ pass_threshold: e.target.value }, ["threshold"])}
            disabled={disabled}
            data-testid={IDS.quizThreshold}
            className={`w-20 ${errors.threshold ? invalidInputClass : ""}`}
          />
          <span className="text-sm text-eh-muted">%</span>
        </div>
      </div>
      <FieldError message={errors.threshold} testId={IDS.stepError("threshold")} />
    </section>
  );
}

/**
 * The type of a SAVED step, shown instead of the selector: the server keeps a stored
 * step's kind (contract §10.1 STEP_IDENTITY_CHANGED), so offering the switch would only
 * lead to a refused save.
 */
function KindLocked({ kind }) {
  const t = useT("admin");
  const Icon = kindIcon(kind);
  return (
    <div data-testid={IDS.stepKindLocked} data-kind={kind} className="space-y-1">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-eh-border-soft bg-eh-ochre/15 px-4 py-2 text-sm font-semibold text-eh-ink">
        <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {kindLabel(t, kind)}
      </span>
      <p className="flex items-center gap-1.5 text-xs text-eh-muted">
        <Lock className="w-3.5 h-3.5" aria-hidden="true" /> {t("path.editor.kindLocked")}
      </p>
    </div>
  );
}

/**
 * One catalogue read for a panel (the session types, the courses, the course quizzes):
 * read when the panel mounts, with its own failure and retry, so a failed catalogue never
 * blocks the rest of the step.
 */
function useCatalogue(read) {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => { setRows(await read()); }, [read]);
  const { loading, loadError, reload } = useAdminLoad(load);
  return { rows, loading, error: loadError, reload };
}

function CatalogueFailure({ onRetry, testId }) {
  const t = useT("admin");
  return (
    <p role="alert" data-testid={testId} className="flex flex-wrap items-center gap-2 text-sm text-rose-800">
      <AlertTriangle className="w-4 h-4" aria-hidden="true" /> {t("path.editor.catalogueFailed")}
      <button type="button" onClick={onRetry} className="underline font-semibold">{t("path.retry")}</button>
    </p>
  );
}

function PracticePanel({ step, patch, disabled, error }) {
  const t = useT("admin");
  const p = step.practice;
  const set = (fields, clear) => patch({ practice: { ...p, ...fields } }, clear);
  return (
    <section aria-labelledby="pathway-practice-heading" className="rounded-2xl border border-eh-border-soft p-4 space-y-3">
      <h3 id="pathway-practice-heading" className="text-sm font-semibold text-eh-ink">{t("path.kind.practice")}</h3>
      <div>
        <label htmlFor="pathway-practice-instructions" className="block text-xs text-eh-muted mb-1">{t("path.editor.practice.instructions")}</label>
        <textarea
          id="pathway-practice-instructions"
          value={p.instructions}
          onChange={(e) => set({ instructions: e.target.value }, ["instructions"])}
          rows={5}
          maxLength={PRACTICE_INSTRUCTIONS_MAX}
          disabled={disabled}
          placeholder={t("path.editor.practice.placeholder")}
          aria-invalid={Boolean(error)}
          data-testid={IDS.practiceInstructions}
          className={`w-full border border-input rounded-md px-3 py-2 text-sm bg-white resize-y ${error ? invalidInputClass : ""}`}
        />
        <FieldError message={error} testId={IDS.stepError("instructions")} />
      </div>
      <label className="flex items-center gap-2 text-sm text-eh-ink">
        <input
          type="checkbox"
          checked={p.submission_required}
          onChange={(e) => set({ submission_required: e.target.checked })}
          disabled={disabled}
          data-testid={IDS.practiceSubmission}
          className="accent-eh-sage-deep"
        />
        {t("path.editor.practice.requireSubmission")}
      </label>
      <p className="text-sm text-eh-muted">
        {t(p.submission_required ? "path.editor.practice.completesOnSubmit" : "path.editor.practice.completesOnDone")}
      </p>
    </section>
  );
}

function LivePanel({ step, patch, schedule, disabled, errors }) {
  const t = useT("admin");
  const l = step.live;
  const set = (fields, clear) => patch({ live: { ...l, ...fields } }, clear);
  const cohort = schedule === "cohort";
  return (
    <section aria-labelledby="pathway-live-heading" data-testid={IDS.livePanel} data-mode={cohort ? "cohort" : "self_paced"} className="rounded-2xl border border-eh-border-soft p-4 space-y-3">
      <h3 id="pathway-live-heading" className="text-sm font-semibold text-eh-ink">{t("path.kind.live")}</h3>
      {cohort ? (
        <CohortTime value={l.starts_at} onChange={(iso) => set({ starts_at: iso }, ["startsAt"])} disabled={disabled} error={errors.startsAt} />
      ) : (
        <SessionTypePicker
          live={l}
          disabled={disabled}
          onPick={(svc) => patch(
            {
              live: { ...l, service_id: svc.id, service: { id: svc.id, name: svc.name, duration_min: svc.duration_min, active: svc.active } },
              duration_minutes: String(svc.duration_min),
            },
            ["duration"],
          )}
        />
      )}
      <div>
        <label htmlFor="pathway-live-location" className="block text-xs text-eh-muted mb-1">{t("path.editor.live.location")}</label>
        <Input
          id="pathway-live-location"
          value={l.location_note}
          onChange={(e) => set({ location_note: e.target.value }, ["location"])}
          maxLength={LOCATION_NOTE_MAX}
          disabled={disabled}
          placeholder={t("path.editor.live.locationPh")}
          aria-invalid={Boolean(errors.location)}
          data-testid={IDS.liveLocation}
          className={errors.location ? invalidInputClass : ""}
        />
        <FieldError message={errors.location} testId={IDS.stepError("location")} />
      </div>
      <p className="text-sm text-eh-muted">{t(cohort ? "path.editor.live.completesOnAttendance" : "path.editor.live.completesOnBooking")}</p>
    </section>
  );
}

function CohortTime({ value, onChange, disabled, error }) {
  const t = useT("admin");
  const zone = browserTimeZone();
  return (
    <div>
      <label htmlFor="pathway-live-starts" className="block text-xs text-eh-muted mb-1">{t("path.editor.live.dateTime")}</label>
      <Input
        id="pathway-live-starts"
        type="datetime-local"
        value={isoToLocalInput(value)}
        onChange={(e) => onChange(localInputToIso(e.target.value))}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby="pathway-live-starts-zone"
        data-testid={IDS.liveStartsAt}
        className={`w-64 ${error ? invalidInputClass : ""}`}
      />
      <p id="pathway-live-starts-zone" className="mt-1 text-xs text-eh-muted">
        {zone ? t("path.editor.live.zone", { zone }) : t("path.editor.live.zoneLocal")}
      </p>
      <FieldError message={error} testId={IDS.stepError("startsAt")} />
    </div>
  );
}

/**
 * "Session type ▾ booked separately": the practice's ACTIVE booking services. A step that
 * already links a service no longer active (or no longer listed) keeps it as a marked
 * option, so opening the editor never silently changes what the step points at; the
 * activation banner names the problem (LIVE_SERVICE_INACTIVE).
 */
function SessionTypePicker({ live, disabled, onPick }) {
  const t = useT("admin");
  const { rows, loading, error, reload } = useCatalogue(listBookingServices);
  const active = (rows || []).filter((svc) => svc.active);
  const current = live.service_id && !active.some((svc) => svc.id === live.service_id)
    ? { id: live.service_id, name: live.service?.name || "" }
    : null;
  const onChange = (id) => {
    const svc = active.find((x) => x.id === id);
    if (svc) onPick(svc);
  };
  return (
    <div>
      <label htmlFor="pathway-live-service" className="block text-xs text-eh-muted mb-1">{t("path.editor.live.sessionType")}</label>
      <div className="flex flex-wrap items-center gap-2">
        <select
          id="pathway-live-service"
          value={live.service_id || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading}
          data-testid={IDS.liveService}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm text-eh-ink"
        >
          <option value="" disabled>{t("path.editor.live.chooseSessionType")}</option>
          {current && (
            <option value={current.id} disabled>
              {t("path.editor.live.serviceUnavailable", { name: current.name || t("path.editor.live.serviceUnnamed") })}
            </option>
          )}
          {active.map((svc) => (
            <option key={svc.id} value={svc.id}>{t("path.editor.live.serviceOption", { name: svc.name, n: svc.duration_min })}</option>
          ))}
        </select>
        <span className="text-sm text-eh-muted">{t("path.editor.live.bookedSeparately")}</span>
      </div>
      {error && <CatalogueFailure onRetry={reload} testId={IDS.liveServiceError} />}
      {rows && active.length === 0 && (
        <p data-testid={IDS.liveServiceNone} className="mt-1 text-xs text-eh-muted">{t("path.editor.live.noServices")}</p>
      )}
    </div>
  );
}

/**
 * The course picker: the practice's PUBLISHED courses (an unpublished one could never be
 * activated, COURSE_NOT_PUBLISHED). A saved course step's course is fixed (contract
 * §10.1), so it is shown, not offered.
 */
function CoursePanel({ step, patch, locked, disabled, error }) {
  const t = useT("admin");
  const c = step.course;
  const info = c.info;
  return (
    <section aria-labelledby="pathway-course-heading" className="rounded-2xl border border-eh-border-soft p-4 space-y-3">
      <h3 id="pathway-course-heading" className="text-sm font-semibold text-eh-ink">{t("path.kind.course")}</h3>
      {locked ? (
        <div className="space-y-1">
          <p data-testid={IDS.courseFixed} className="text-sm font-semibold text-eh-ink">{info?.title || t("path.editor.course.missing")}</p>
          <p className="flex items-center gap-1.5 text-xs text-eh-muted">
            <Lock className="w-3.5 h-3.5" aria-hidden="true" /> {t("path.editor.course.locked")}
          </p>
        </div>
      ) : (
        <CoursePicker
          courseId={c.course_id}
          disabled={disabled}
          error={error}
          onPick={(course) => patch(
            {
              course: {
                course_id: course.id,
                info: {
                  id: course.id,
                  title: course.title,
                  published: Boolean(course.published),
                  lessons_count: course.lessons.length,
                  price_cents: Number(course.price_cents) || 0,
                  currency: course.currency || "",
                  cover_url: course.cover_url || "",
                },
              },
              duration_minutes: String(courseDurationMinutes(course)),
            },
            ["course", "duration"],
          )}
        />
      )}
      {info && info.price_cents > 0 && (
        <p data-testid={IDS.coursePaidWarning} role="note" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" /> {t("path.editor.course.paidWarning")}
        </p>
      )}
      <p className="text-sm text-eh-muted">{t("path.editor.course.completes")}</p>
    </section>
  );
}

function CoursePicker({ courseId, disabled, error, onPick }) {
  const t = useT("admin");
  const { rows, loading, error: loadError, reload } = useCatalogue(listCourses);
  const published = (rows || []).filter((course) => course.published);
  return (
    <div>
      <label htmlFor="pathway-course-select" className="block text-xs text-eh-muted mb-1">{t("path.editor.course.pick")}</label>
      <select
        id="pathway-course-select"
        value={courseId || ""}
        onChange={(e) => { const course = published.find((x) => x.id === e.target.value); if (course) onPick(course); }}
        disabled={disabled || loading}
        aria-invalid={Boolean(error)}
        data-testid={IDS.coursePicker}
        className={`h-10 w-full max-w-md rounded-md border border-input bg-white px-3 text-sm text-eh-ink ${error ? invalidInputClass : ""}`}
      >
        <option value="" disabled>{t("path.editor.course.choose")}</option>
        {published.map((course) => (
          <option key={course.id} value={course.id}>{t("path.editor.course.option", { title: course.title, n: course.lessons.length })}</option>
        ))}
      </select>
      <FieldError message={error} testId={IDS.stepError("course")} />
      {loadError && <CatalogueFailure onRetry={reload} testId={IDS.coursePickerError} />}
      {rows && published.length === 0 && (
        <p data-testid={IDS.coursePickerNone} className="mt-1 text-xs text-eh-muted">{t("path.editor.course.none")}</p>
      )}
    </div>
  );
}

/**
 * "Import from a course quiz" (contract §10.0.6, §10.5): the MCQs of one of the
 * practice's course quiz lessons, APPENDED to this quiz as a copy. The server leaves out
 * what a pathway quiz cannot hold (open questions, MCQs outside 2..6 choices) and counts
 * it in `skipped_count`, which is said after the import.
 */
function QuizImport({ onImport, disabled }) {
  const t = useT("admin");
  const { rows, loading, error, reload } = useCatalogue(listCourseQuizzes);
  const [chosen, setChosen] = useState("");
  const [result, setResult] = useState(null);   // { added, skipped } of the last import
  const keyOf = (q) => `${q.course_id}:${q.lesson_id}`;
  const quizzes = rows || [];

  const run = () => {
    const quiz = quizzes.find((q) => keyOf(q) === chosen);
    if (!quiz) return;
    onImport(quiz);
    setResult({ added: quiz.questions.length, skipped: quiz.skipped_count });
  };

  return (
    <div data-testid={IDS.quizImport} className="rounded-xl bg-eh-cream/60 border border-eh-border-soft px-3 py-3 space-y-2">
      <label htmlFor="pathway-quiz-import" className="flex items-center gap-1.5 text-sm font-semibold text-eh-ink">
        <Download className="w-4 h-4" aria-hidden="true" /> {t("path.editor.quiz.import")}
      </label>
      {error && <CatalogueFailure onRetry={reload} testId={IDS.quizImportError} />}
      {rows && quizzes.length === 0 && <p data-testid={IDS.quizImportNone} className="text-xs text-eh-muted">{t("path.editor.quiz.importNone")}</p>}
      {quizzes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            id="pathway-quiz-import"
            value={chosen}
            onChange={(e) => { setChosen(e.target.value); setResult(null); }}
            disabled={disabled || loading}
            data-testid={IDS.quizImportSelect}
            className="h-9 min-w-0 flex-1 rounded-md border border-input bg-white px-3 text-sm text-eh-ink"
          >
            <option value="" disabled>{t("path.editor.quiz.importChoose")}</option>
            {quizzes.map((q) => (
              <option key={keyOf(q)} value={keyOf(q)}>
                {t("path.editor.quiz.importOption", { course: q.course_title, quiz: q.title, n: q.questions.length })}
              </option>
            ))}
          </select>
          <Button variant="outline" onClick={run} disabled={disabled || !chosen} data-testid={IDS.quizImportRun}>
            {t("path.editor.quiz.importRun")}
          </Button>
        </div>
      )}
      {result && (
        <p role="status" data-testid={IDS.quizImportResult} className="text-xs text-eh-ink">
          {t(result.added === 1 ? "path.editor.quiz.importedOne" : "path.editor.quiz.imported", { n: result.added })}
          {result.skipped > 0 && (
            <span data-testid={IDS.quizImportSkipped} className="block text-amber-800">
              {t(result.skipped === 1 ? "path.editor.quiz.importSkippedOne" : "path.editor.quiz.importSkipped", { n: result.skipped })}
            </span>
          )}
        </p>
      )}
    </div>
  );
}
